/*----------------------------------
- HOOK: PREVIEW DRAWER OPENER
----------------------------------*/

/**
 * Moved out of /domains (batch 7) with the old frame's look files, which batch 4 deleted once /registrars
 * and /auctions moved to the marketing `Drawer`. This one is logic, not look: /domains and Home read it too,
 * and so do the /registrars and /auctions drawers.
 *
 * @docs docs/features/product-domain-listings
 * @docs docs/features/auctions
 * @adr  ADR-0028
 * @rule The control that opened a public preview drawer gets the focus back when the drawer closes,
 *       except through the call to action, which hands the focus to the auth modal it opens. One
 *       hook for every public preview drawer (`/domains*`, Home, `/auctions`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

// Types
import type { PreviewDrawerCloseReason } from './useLazyPreviewDrawer';

/*----------------------------------
- HOOK
----------------------------------*/

const usePreviewDrawerOpener = () => {
    /** The row control that opened the drawer, focused again when it closes. */
    const openerRef = React.useRef<HTMLElement | null>(null);

    /** Called on a row click: the focused element is the row's control. */
    const rememberOpener = React.useCallback(() => {
        openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }, []);

    /** Called on every close; walking to a neighbour keeps the opener. */
    const returnFocus = React.useCallback((reason: PreviewDrawerCloseReason) => {
        // The CTA hands focus to the auth modal it opens; every other close returns it to the row.
        // Known overlap (review m-14): on `/domains` the marketing Drawer's `useOverlay` also restores the focus it
        // saved on open, a frame later, `cta` included; both target the same row control, and after `cta` the auth
        // modal pulls focus back into itself, so the visitor sees one move. Let the overlay skip its own return
        // when its caller manages focus if the two ever disagree.
        if (reason !== 'cta') openerRef.current?.focus();
        openerRef.current = null;
    }, []);

    return { rememberOpener, returnFocus };
};

export default usePreviewDrawerOpener;
